// Townsfolk from the single-player town. For now they live on each player's own screen (no server):
// they stand at home, say things now and then, and talk to you. Server-driven NPCs come with quests and combat.
import { PEOPLE, TOWN, JOBS } from '../../legacy/art.gen.js';
import { fromLegacy, sanitizeKit } from '../../../shared/kit.js';
import { SUB } from '../../../shared/constants.js';
import type { ZoneMap } from '../../../shared/map.js';
import type { Look } from '../../../shared/look.js';
import type { BubbleContent } from '../render/ui2d.js';

interface PersonDef { name: string; look: Record<string, unknown>; at: [number, number]; face?: string; say: string[]; think: string[]; talk: [string, string, string][] }

export interface Npc {
  name: string; look: Look; x: number; y: number; dir: string; home: string;
  say: string[]; think: string[]; talk: [string, string, string][];
  bubble: { kind: 'say' | 'think'; content: BubbleContent; until: number } | null;
  next: number; met: boolean; face: string | null;
  /** Hours they're out and about [from, to) on the shared clock; outside them they've gone home. */
  hours: [number, number]; alpha: number;
  /** Strolling about near where they live. */
  hx: number; hy: number; tx: number; ty: number; walking: boolean; nextWalk: number; range: number;
}

// When the townsfolk are out. Everyone not listed keeps 6 am to 9 pm.
const HOURS: Record<string, [number, number]> = {
  Boy: [7, 20], Girl: [7, 20], Grandma: [7, 20], Grandpa: [7, 19.5], Knight: [0, 24], Bard: [9, 23.5], Fisher: [5, 19],
  Monk: [5, 19], Thief: [10, 24], Barista: [6, 20], Samurai: [6, 20.5],
};
const out = (h: number, [a, b]: [number, number]) => a <= b ? h >= a && h < b : h >= a || h < b;

// Night folk: they come out when the lamps are lit.
const pre = (list: { name: string; look: Record<string, unknown> }[], name: string) => list.find(p => p.name === name)!.look;
const NIGHT: PersonDef[] = [
  { name: 'Lamplighter Tomas', look: { ...pre(TOWN, 'Man'), hat: 'beanie', offhand: 'lantern', hairColor: 'silver' }, at: [12, 13], face: 'down',
    say: ['Lamps are lit!', 'Lovely evening.'], think: ['sparkle', 'Moonflowers...'],
    talk: [['n', 'smile', 'Evening, friend. Every lamp in town, lit by hand. Takes me an hour.'], ['n', 'wink', 'When the lamps glow, the moonflowers open. Bring me three and I\'ll brew you something special.'], ['p', 'happy', "I'll keep an eye out."]] },
  { name: 'Stargazer Wren', look: { ...pre(JOBS, 'Black Mage') }, at: [24, 6], face: 'up',
    say: ['Look up!', 'A shooting star!'], think: ['star', 'Seven, eight...'],
    talk: [['n', 'excited', "Shh! I'm counting stars. I've reached four thousand and... oh no."], ['n', 'laugh', 'Start again tomorrow, I suppose.'], ['n', 'smile', 'If you see a shooting star, make a cozy wish.']] },
  { name: 'Moth Merchant', look: { ...pre(TOWN, 'Barista'), hat: 'hood' }, at: [24, 18], face: 'down',
    say: ['Night wares!', 'Psst... moon tea?'], think: ['coin', 'Sleepy...'],
    talk: [['n', 'wink', 'The day shop closes, the night shop opens. Candles, moon tea, odd little things.'], ['n', 'smile', "Come back when I've unpacked. Trading is coming soon."]] },
  { name: 'Old Nell', look: { ...pre(TOWN, 'Grandma'), hat: 'bucket', weapon: 'rod' }, at: [8, 21], face: 'down',
    say: ['The moon fish bite now.', '...patience.'], think: ['dots', 'Big one...'],
    talk: [['n', 'neutral', 'Fish are different at night. Shy, silvery things.'], ['n', 'smile', "When you've a rod and a free evening, sit with me a while."]] },
];
const NIGHT_HOURS: [number, number] = [20, 5.5];

// Who works inside each building (quest givers you've heard about).
const INDOORS: Record<string, PersonDef[]> = {
  bakery: [{ name: 'Baker Rosa', look: { ...pre(TOWN, 'Baker') }, at: [6, 3], face: 'down', say: ['Warm loaves!', 'Wipe your feet, dear.'], think: ['heart', 'Mushroom tarts...'],
    talk: [['n', 'happy', "Welcome in! Smell that? Honey loaf, fresh out of the oven."], ['n', 'wink', 'If you find red mushrooms out by the trees, I bake a mean tart.'], ['p', 'smile', "I'll keep my eyes open."]] }],
  guild: [{ name: 'Guildmaster Oak', look: { ...pre(JOBS, 'Knight') }, at: [3, 4], face: 'down', say: ['Find a party!', 'Strength in numbers.'], think: ['star', 'Recruits...'],
    talk: [['n', 'smile', 'This is where adventurers find each other. Check the board, or the Party Finder.'], ['n', 'happy', 'Nobody adventures alone in Tiny Town.']] }],
  florist: [{ name: 'Florist Ivy', look: { ...pre(TOWN, 'Florist') }, at: [5, 2], face: 'down', say: ['Fresh blooms!', 'Mind the petals.'], think: ['heart', 'sparkle'],
    talk: [['n', 'happy', 'Welcome to my little shop! Every flower here was picked this morning.'], ['n', 'wink', 'At night the moonflowers open. Bring me some sometime.']] }],
};
// Out in the districts and the Meadowlands.
const both = (n: string) => pre([...TOWN, ...JOBS] as { name: string; look: Record<string, unknown> }[], n);
Object.assign(INDOORS, {
  harbor: [
    { name: 'Harbormaster Gil', look: { ...both('Sailor') }, at: [6, 10], face: 'down', say: ['Tide\'s turning.', 'Mind the ropes!'], think: ['dots', 'Gulls...'],
      talk: [['n', 'smile', 'Welcome to the harbor! Boats come and go with the tide.'], ['n', 'wink', 'One day a ferry will take you over the water. Not today, mind.']] },
    { name: 'Fishwife Marla', look: { ...both('Fisher') }, at: [15, 11], face: 'down', say: ['Fresh catch!', 'Smell that sea air.'], think: ['heart', 'Mackerel...'],
      talk: [['n', 'happy', "Fresh off the boats this morning, love."], ['n', 'smile', 'Bring a rod down to the end of the pier sometime.']] },
  ],
  artisan: [
    { name: 'Smith Bruna', look: { ...both('Berserker') }, at: [9, 10], face: 'down', say: ['Clang!', 'Hot iron, mind your fingers.'], think: ['sparkle', 'Beetle shells...'],
      talk: [['n', 'neutral', 'Bramble beetles out in the Meadowlands have shells tough as tin.'], ['n', 'smile', 'Bring me some, and one day I\'ll make you something sturdy.']] },
    { name: 'Weaver Lin', look: { ...both('White Mage') }, at: [27, 10], face: 'down', say: ['So soft...', 'Loop, pull, loop.'], think: ['heart', 'Bunny fluff!'],
      talk: [['n', 'happy', 'Meadow bunnies shed the softest tufts. I spin them into yarn.'], ['p', 'smile', "I'll keep that in mind."]] },
  ],
  cottages: [
    { name: 'Neighbor Pim', look: { ...both('Gardener') }, at: [12, 12], face: 'down', say: ['Morning, neighbor!', 'Roses need water.'], think: ['heart', 'Tea time?'],
      talk: [['n', 'happy', 'Every door on this lane opens to your own little cottage. Magic, isn\'t it?'], ['n', 'wink', 'Decorating comes soon. I hear you can buy furniture at the carpenter\'s.']] },
  ],
  gate: [
    { name: 'Gatewarden Hale', look: { ...both('Knight') }, at: [16, 12], face: 'down', say: ['Safe travels.', 'All quiet.'], think: ['dots', 'Beetles again...'],
      talk: [['n', 'neutral', 'Past this gate are the Meadowlands. Puffballs, bunnies, and the odd grumpy beetle.'], ['n', 'smile', 'If you get knocked about, the wayshrine will patch you up. Dash out of the way of a hit!'], ['p', 'happy', "I'll be careful."]] },
  ],
  meadow: [
    { name: 'Farmer Odo', look: { ...both('Farmer') }, at: [47, 36], face: 'down', say: ['Shoo, bunnies!', 'Good soil, this.'], think: ['heart', 'Carrots...'],
      talk: [['n', 'laugh', 'Those bunnies are after my carrots again! Gently does it, mind. They just get grumpy.'], ['n', 'smile', 'Once you\'re a sturdier adventurer, the roads go further. They say there\'s a whole wide world out there.']] },
  ],
} as Record<string, PersonDef[]>);
const INDOOR_HOURS: [number, number] = [6, 21];

export interface Dialogue { npc: Npc; lines: [string, string, string][]; i: number; shown: number; t0: number; skip: boolean }

const pick = <T>(l: T[]): T => l[Math.floor(Math.random() * l.length)];

/** Townsfolk written for the old engine are drawn with the townkit (converted look). */
export function makeNpcs(map: ZoneMap): Npc[] {
  const make = (d: PersonDef, hours: [number, number]): Npc => {
    const [x, y] = map.nearestFreeSub(d.at[0], d.at[1]);
    return { name: d.name, look: (d.look as { v?: number }).v === 2 ? sanitizeKit(d.look)! : fromLegacy(d.look), x: x / SUB, y: y / SUB, dir: d.face || 'down', home: d.face || 'down', say: d.say, think: d.think, talk: d.talk,
      bubble: null, next: performance.now() + 3000 + Math.random() * 12000, met: false, face: null, hours, alpha: 1,
      hx: x / SUB, hy: y / SUB, tx: x / SUB, ty: y / SUB, walking: false, nextWalk: performance.now() + 1500 + Math.random() * 6000, range: map.id === 'town' || OUTSIDE.has(map.id) ? 36 : 14 };
  };
  if (map.id !== 'town') return (INDOORS[map.id] ?? []).map(d => make(d, INDOOR_HOURS));
  return [...(PEOPLE as PersonDef[]).map(d => make(d, HOURS[d.name] ?? [6, 21])), ...NIGHT.map(d => make(d, NIGHT_HOURS))];
}

/** Townsfolk fade out when they head home and fade in when they come out. `instant` skips the fade (on joining). */
export function scheduleNpcs(npcs: Npc[], hour: number, dt: number, instant = false): void {
  for (const n of npcs) {
    const want = out(hour, n.hours) ? 1 : 0;
    n.alpha = instant ? want : want > n.alpha ? Math.min(1, n.alpha + dt * 0.5) : want < n.alpha ? Math.max(0, n.alpha - dt * 0.5) : want;   // (equal: stay put, or they shimmer 100% ↔ 99% every frame)
    if (n.alpha < 0.5) n.bubble = null;
  }
}

const OUTSIDE = new Set(['harbor', 'artisan', 'cottages', 'gate', 'meadow']);
const NPC_SPEED = 26;   // pixels a second: an unhurried stroll

/** Townsfolk wander a little around their spot, pause, look about, and wander back. They stop when you come
 *  close (so they're easy to talk to) and while they're talking. */
export function walkNpcs(npcs: Npc[], dt: number, now: number, map: ZoneMap, px: number, py: number, busy: Npc | null): void {
  for (const n of npcs) {
    if (n === busy || n.alpha < 1) { n.walking = false; continue; }
    const near = Math.hypot(n.x - px, n.y - py) < 30;
    if (n.walking) {
      if (near) { n.walking = false; n.nextWalk = now + 2500; n.dir = dirToward(px - n.x, py - n.y); continue; }
      const dx = n.tx - n.x, dy = n.ty - n.y, d = Math.hypot(dx, dy), step = Math.min(d, NPC_SPEED * dt);
      if (d < 0.6) { n.walking = false; n.nextWalk = now + 2500 + Math.random() * 6500; if (Math.random() < 0.5) n.dir = n.home; continue; }
      const nx = n.x + dx / d * step, ny = n.y + dy / d * step;
      if (!map.canStandSub(Math.round(nx * SUB), Math.round(ny * SUB))) { n.walking = false; n.nextWalk = now + 1500; continue; }
      n.x = nx; n.y = ny; n.dir = dirToward(dx, dy);
    } else if (now > n.nextWalk && !near && !n.bubble) {
      n.nextWalk = now + 3000 + Math.random() * 5000;
      if (Math.random() < 0.65) {
        const a = Math.random() * Math.PI * 2, r = n.range * (0.3 + Math.random() * 0.7);
        const tx = n.hx + Math.cos(a) * r, ty = n.hy + Math.sin(a) * r * 0.7;
        if (map.canStandSub(Math.round(tx * SUB), Math.round(ty * SUB))) { n.tx = tx; n.ty = ty; n.walking = true; }
      }
    }
  }
}

export function updateNpcs(npcs: Npc[], now: number, px: number, py: number, busy: Npc | null): void {
  const live = npcs.filter(n => n.bubble && n.bubble.until > now).length;
  for (const n of npcs) {
    if (n.bubble && n.bubble.until < now) n.bubble = null;
    if (n === busy || n.alpha < 1) continue;
    if (now > n.next) {
      n.next = now + 7000 + Math.random() * 12000;
      if (live < 4 && Math.hypot(n.x - px, n.y - py) < 200) {
        if (Math.random() < 0.5) n.bubble = { kind: 'say', content: pick(n.say), until: now + 2800 };
        else { const t = pick(n.think); n.bubble = { kind: 'think', content: t.includes(' ') || t.includes('.') ? t : { icon: t }, until: now + 3200 }; }
      }
      if (Math.random() < 0.3) n.dir = Math.random() < 0.6 ? n.home : pick(['left', 'right', 'down']);
    }
  }
}

const VEC: Record<string, [number, number]> = { up: [0, -1], down: [0, 1], left: [-1, 0], right: [1, 0] };
export function nearbyNpc(npcs: Npc[], px: number, py: number, dir: string): Npc | null {
  const f = VEC[dir]; let best: Npc | null = null, bd = 1e9;
  for (const n of npcs) {
    if (n.alpha < 0.6) continue;
    const dx = n.x - px, dy = n.y - py, d = Math.hypot(dx, dy);
    if (d > 26) continue;
    const facing = (dx * f[0] + dy * f[1]) / (d || 1), score = d - facing * 10;
    if (facing > -0.2 && score < bd) { bd = score; best = n; }
  }
  return best;
}

export const dirToward = (dx: number, dy: number): string => Math.abs(dx) > Math.abs(dy) ? (dx > 0 ? 'right' : 'left') : (dy > 0 ? 'down' : 'up');

export function startTalk(n: Npc, px: number, py: number, now: number): Dialogue {
  n.bubble = null; n.dir = dirToward(px - n.x, py - n.y);
  const lines = n.met ? [n.talk.filter(l => l[0] === 'n').slice(-1)[0]] : n.talk;
  return { npc: n, lines, i: 0, shown: 0, t0: now, skip: false };
}

/** Returns null when the conversation ends. */
export function advance(d: Dialogue, now: number): Dialogue | null {
  const line = d.lines[d.i];
  if (d.shown < line[2].length) { d.shown = line[2].length; d.skip = true; return d; }
  d.i++; d.shown = 0; d.t0 = now; d.skip = false;
  if (d.i >= d.lines.length) { d.npc.met = true; d.npc.face = null; d.npc.dir = d.npc.home; d.npc.bubble = { kind: 'think', content: { icon: 'heart' }, until: now + 1600 }; return null; }
  return d;
}
